import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useAuth from "@/hooks/useAuth";
import companiesApi from "@/api/companiesApi";
import ApiError from "@/model/ApiError";
import CompanyResponse from "@/model/CompanyResponse";
import "@/pages/CompanyLayout/CompanyLayout.scss";

function tabLinkClassName({ isActive }: { isActive: boolean }): string {
  return isActive
    ? "company-layout__tab company-layout__tab--active"
    : "company-layout__tab";
}

function CompanyLayout() {
  const { t } = useTranslation();
  const { session } = useAuth();
  const { id } = useParams<{ id: string }>();

  const [company, setCompany] = useState<CompanyResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<"notFound" | "generic" | null>(null);

  useEffect(() => {
    if (!session || !id) {
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    companiesApi
      .getCompanyById(session.accessToken, id)
      .then((result) => {
        if (!cancelled) {
          setCompany(result);
        }
      })
      .catch((err) => {
        if (cancelled) {
          return;
        }
        if (err instanceof ApiError && err.status === 404) {
          setError("notFound");
        } else {
          setError("generic");
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [session, id]);

  if (!session || !id) {
    return null;
  }

  return (
    <div className="company-layout">
      {isLoading && <p>{t("companies.layout.loading")}</p>}
      {!isLoading && error === "notFound" && (
        <p role="alert">{t("companies.layout.notFound")}</p>
      )}
      {!isLoading && error === "generic" && (
        <p role="alert">{t("companies.layout.error")}</p>
      )}

      {!isLoading && !error && company && (
        <>
          <header className="company-layout__header">
            <div>
              <h1>{company.name ?? "—"}</h1>
              {company.status && (
                <span
                  className={`status-badge status-badge--${company.status.toLowerCase()}`}
                >
                  {t(`companies.status.${company.status}`)}
                </span>
              )}
            </div>
            <div className="company-layout__header-actions">
              <Link to={`/companies/${id}/edit`}>
                {t("companies.layout.editLink")}
              </Link>
              <Link to="/companies">{t("companies.layout.backLink")}</Link>
            </div>
          </header>

          <nav className="company-layout__tabs">
            <NavLink to={`/companies/${id}`} end className={tabLinkClassName}>
              {t("companies.layout.tabs.overview")}
            </NavLink>
            <NavLink
              to={`/companies/${id}/users`}
              className={tabLinkClassName}
            >
              {t("companies.layout.tabs.users")}
            </NavLink>
            <NavLink
              to={`/companies/${id}/properties`}
              className={tabLinkClassName}
            >
              {t("companies.layout.tabs.properties")}
            </NavLink>
            <NavLink
              to={`/companies/${id}/bookings`}
              className={tabLinkClassName}
            >
              {t("companies.layout.tabs.bookings")}
            </NavLink>
          </nav>

          <div className="company-layout__panel">
            <Outlet context={{ company }} />
          </div>
        </>
      )}
    </div>
  );
}

export default CompanyLayout;
